/**
 * FrHtmlViewer — one France ANSM (BDPM) label document (RCP or Notice, picked in
 * the drug page's "Document set" dropdown): the shared collapsible TOCSidebar
 * and the stored French HTML (sanitised at parse time and again here) in our
 * own type and table styles.
 *
 * English / Français toggle, translated like Google Translate does it: only the
 * blocks in (or near) view, a few at a time, each swapping to English as it
 * arrives. Every block goes through /api/fr_html/translate (Redis, then the
 * ch_translation_cache DB table), so a paragraph is paid for ONCE, ever, across
 * every label and user; after that it is instant.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { authApi } from '../../services/authApi';
import TOCSidebar from './TOCSidebar';
import type { TOCEntry } from '../../types/viewer';

interface FrSec { id: number; number: string; title: string; title_en: string | null;
                  level: number; html: string }
type Lang = 'en' | 'fr';

const CONCURRENCY = 6;
const LANG_KEY = 'frHtmlViewer.lang';

const enByText = new Map<string, Promise<string>>();
function english(text: string, docId: number, secId: number | null): Promise<string> {
  let p = enByText.get(text);
  if (!p) {
    p = authApi.translateFrHtmlText(text, true, docId, secId);   // inline <b>/<i>/<sup> and table grids survive
    p.catch(() => enByText.delete(text));
    enByText.set(text, p);
  }
  return p;
}

const CSS = `
.fr-label { color:#1e293b; font-size:14px; line-height:1.6; }
.fr-label p { margin:0 0 .55em; }
.fr-label ul, .fr-label ol { margin:0 0 .6em 1.4em; }
.fr-label ul { list-style:disc; } .fr-label ol { list-style:decimal; }
.fr-label li { margin:.15em 0; }
.fr-label .fr-tbl { overflow-x:auto; margin:.4em 0 .9em; }
.fr-label table { border-collapse:collapse; width:100%; font-size:12.5px; line-height:1.4; }
.fr-label td, .fr-label th { border:1px solid #cbd5e1; padding:4px 7px; vertical-align:top; text-align:left; }
.fr-label tr:first-child td, .fr-label th { background:#f1f5f9; font-weight:600; }
.fr-label td p, .fr-label th p { margin:0; }
.fr-label .fr-pending { opacity:.45; transition:opacity .2s; }
.fr-label mark[data-kw] { background:#fef08a; border-radius:2px; padding:0 1px; }
`;

// Keyword-search hits are wrapped in <mark data-kw>; stripped again before a
// block is translated or stored so they never reach the translation cache key.
const MARK_RE = /<mark data-kw="">([\s\S]*?)<\/mark>/g;
const unmark = (html: string) => html.replace(MARK_RE, '$1');

/** Wrap every case-insensitive hit of ``terms`` under ``root`` in
 *  <mark data-kw> — text nodes only, so tags and attributes are never touched. */
function markTerms(root: HTMLElement, terms: string[]): void {
  const ts = terms.map((t) => t.trim()).filter((t) => t.length > 1);
  if (!ts.length) return;
  const re = new RegExp(`(${ts.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'i');
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const hits: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!n.parentElement?.closest('mark[data-kw]') && re.test(n.nodeValue || '')) hits.push(n as Text);
  }
  for (const n of hits) {
    const frag = document.createDocumentFragment();
    (n.nodeValue || '').split(new RegExp(re.source, 'gi')).forEach((part, i) => {
      if (!part) return;
      if (i % 2) {
        const m = document.createElement('mark');
        m.dataset['kw'] = '';
        m.textContent = part;
        frag.appendChild(m);
      } else frag.appendChild(document.createTextNode(part));
    });
    n.replaceWith(frag);
  }
}

/** Wrap each table so a wide one scrolls on its own, not the page. */
function prepare(html: string): string {
  return DOMPurify.sanitize(html).replace(/<table/g, '<div class="fr-tbl"><table').replace(/<\/table>/g, '</table></div>');
}

/** The translation units of a rendered body: whole tables (one call, the grid
 *  is kept), and paragraphs / list items outside tables. A list item holding
 *  its own paragraphs or sub-list is left to those children.
 *  ponytail: loose text sitting beside a nested sub-list in the same <li> stays
 *  French; split it into its own unit if a real label shows it. */
function units(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('.fr-body table, .fr-body p, .fr-body li'))
    .filter((el) => el.tagName === 'TABLE'
      || (!el.closest('table') && !(el.tagName === 'LI' && el.querySelector('p, ul, ol'))
          && (el.textContent || '').trim().length > 1));
}

/** Sections → TOCSidebar rows. ``page_start`` is the section's ORDINAL: the
 *  sidebar navigates only rows with one, and there are no pages in HTML. */
function tocEntries(secs: FrSec[], lang: Lang): TOCEntry[] {
  const stack: FrSec[] = [];
  return secs.map((s, i) => {
    while (stack.length && stack[stack.length - 1]!.level >= s.level) stack.pop();
    const parent = stack[stack.length - 1];
    stack.push(s);
    return {
      id: s.id, section_number: s.number || null, section_number_local: s.number || null,
      title: lang === 'en' && s.title_en ? s.title_en : s.title, title_src: s.title, level: s.level,
      parent_section_number: parent?.number || null, parent_section_id: parent?.id ?? null,
      page_start: i + 1, page_end: i + 1, section_type: null,
    };
  });
}

function savedLang(): Lang {
  try { return localStorage.getItem(LANG_KEY) === 'fr' ? 'fr' : 'en'; } catch { return 'en'; }
}

export default function FrHtmlViewer({ docId, updated, highlightTerm }: {
  docId: number; updated?: string | null;
  /** Keyword-search term (``?highlight=``): marked in English, and its French
   *  translation marked in French; the view scrolls to the first hit once. */
  highlightTerm?: string | null;
}) {
  const [secs, setSecs] = useState<FrSec[] | null>(null);
  const [lang, setLang] = useState<Lang>(savedLang);
  const [busy, setBusy] = useState(0);
  const [failed, setFailed] = useState(0);
  const [active, setActive] = useState<number | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const original = useRef(new Map<HTMLElement, string>());     // French innerHTML, to switch back
  const [srcTerm, setSrcTerm] = useState('');                  // highlightTerm in French
  const [enTerm, setEnTerm] = useState('');                    // highlightTerm in English
  const terms = useMemo(() => [highlightTerm || '', srcTerm, enTerm], [highlightTerm, srcTerm, enTerm]);
  const termsRef = useRef(terms);
  termsRef.current = terms;
  const scrolled = useRef(false);
  const applyMarks = (el: HTMLElement) => {
    markTerms(el, termsRef.current);
    if (scrolled.current) return;
    const hit = body.current?.querySelector('mark[data-kw]');
    if (hit) { hit.scrollIntoView({ block: 'center' }); scrolled.current = true; }
  };

  useEffect(() => {
    // Both directions (cached server-side), so the term is marked in whichever
    // mode is on screen whether the user typed English or French.
    let alive = true;
    setSrcTerm(''); setEnTerm('');
    const clean = (t: string) => t.replace(/<[^>]+>/g, '').trim().replace(/[.;:]+$/, '');
    if (highlightTerm?.trim()) {
      authApi.translateTermToSource('fr_html', highlightTerm, docId)
        .then((t) => { if (alive) setSrcTerm(clean(t)); }).catch(() => { /* typed term still marked */ });
      authApi.translateFrHtmlText(highlightTerm, false, docId, null)
        .then((t) => { if (alive) setEnTerm(clean(t)); }).catch(() => { /* typed term still marked */ });
    }
    return () => { alive = false; };
  }, [highlightTerm, docId]);

  useEffect(() => {
    let alive = true;
    setSecs(null);
    authApi.getFrHtmlSections(docId).then((d) => { if (alive) setSecs(d.sections || []); })
      .catch(() => { if (alive) setSecs([]); });
    return () => { alive = false; };
  }, [docId]);

  const choose = (l: Lang) => {
    setLang(l);
    try { localStorage.setItem(LANG_KEY, l); } catch { /* per-browser convenience only */ }
  };

  // English: translate blocks as they come near the viewport; French: restore.
  useEffect(() => {
    const root = body.current;
    if (!root || !secs?.length) return;
    if (lang === 'fr') {
      original.current.forEach((html, el) => { el.innerHTML = html; el.classList.remove('fr-pending'); });
      original.current.clear();
      setBusy(0); setFailed(0);
      return;
    }
    let alive = true;
    let running = 0;
    const queue: HTMLElement[] = [];
    const pump = () => {
      while (alive && running < CONCURRENCY && queue.length) {
        const el = queue.shift()!;
        if (original.current.has(el)) continue;
        const fr = unmark(el.innerHTML);
        const isTable = el.tagName === 'TABLE';
        const secId = Number((el.closest('[data-sec]') as HTMLElement | null)?.dataset['sec']) || null;
        running++; setBusy((n) => n + 1);
        el.classList.add('fr-pending');
        english(isTable ? `<table>${fr}</table>` : fr, docId, secId)
          .then((en) => {
            if (!alive) return;
            const clean = DOMPurify.sanitize(en);
            const html = isTable
              ? (new DOMParser().parseFromString(clean, 'text/html').querySelector('table')?.innerHTML ?? '')
              : clean;
            if (html.trim()) { original.current.set(el, fr); el.innerHTML = html; applyMarks(el); }
          })
          .catch(() => { if (alive) setFailed((n) => n + 1); })        // block stays French
          .finally(() => {
            el.classList.remove('fr-pending');
            running--; setBusy((n) => Math.max(0, n - 1));
            pump();
          });
      }
    };
    const io = new IntersectionObserver((hits) => {
      for (const h of hits) {
        if (!h.isIntersecting) continue;
        io.unobserve(h.target);
        queue.push(h.target as HTMLElement);
      }
      pump();
    }, { root, rootMargin: '600px 0px' });
    units(root).forEach((el) => io.observe(el));
    return () => { alive = false; io.disconnect(); setBusy(0); };
  }, [lang, secs, docId]);

  // Mark the search term in whatever is on screen (after a language switch or
  // once the French term arrives); translated blocks mark themselves on arrival.
  useEffect(() => {
    if (body.current && secs?.length) applyMarks(body.current);
  }, [secs, lang, terms]);   // eslint-disable-line react-hooks/exhaustive-deps

  const entries = useMemo(() => tocEntries(secs || [], lang), [secs, lang]);

  const onScroll = () => {
    const top = body.current?.getBoundingClientRect().top ?? 0;
    let id: number | null = null;
    for (const el of Array.from(body.current?.querySelectorAll<HTMLElement>('[data-sec]') || [])) {
      if (el.getBoundingClientRect().top - top > 40) break;      // first section still below the fold
      id = Number(el.dataset['sec']);
    }
    setActive(id);
  };

  if (secs === null) return <div className="p-6 text-sm text-slate-400">Loading French label…</div>;
  if (!secs.length) return <div className="p-6 text-sm text-slate-500">This French (ANSM) document has no sections.</div>;

  return (
    <div className="h-full flex flex-col min-h-0">
      <style>{CSS}</style>
      <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center gap-3 text-xs flex-shrink-0">
        <div className="inline-flex rounded-md border border-slate-300 bg-white p-0.5" role="group" aria-label="Language">
          {(['en', 'fr'] as Lang[]).map((l) => (
            <button key={l} onClick={() => choose(l)}
                    className={`px-2.5 py-0.5 rounded ${lang === l ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {l === 'en' ? 'English' : 'Français (original)'}
            </button>
          ))}
        </div>
        {lang === 'en' && busy > 0 && <span className="text-indigo-600">Translating…</span>}
        {lang === 'en' && failed > 0 && busy === 0 && (
          <span className="text-amber-700">{failed} block{failed > 1 ? 's' : ''} could not be translated (shown in French)</span>
        )}
        {updated && <span className="ml-auto text-slate-500">ANSM · updated {updated}</span>}
      </div>
      <div className="flex-1 min-h-0 flex">
        <div className="w-72 flex-shrink-0 overflow-y-auto border-r border-slate-200 hidden md:block">
          <TOCSidebar
            entries={entries}
            currentPage={0}
            activeSectionOverride={active}
            onNavigate={(e) => document.getElementById(`fr-sec-${e.id}`)?.scrollIntoView({ block: 'start' })}
          />
        </div>
        <div ref={body} onScroll={onScroll} className="fr-label flex-1 min-w-0 overflow-y-auto px-6 py-4">
          {secs.map((s) => {
            const H = (s.level <= 1 ? 'h2' : s.level === 2 ? 'h3' : 'h4') as 'h2' | 'h3' | 'h4';
            return (
              <section key={s.id} id={`fr-sec-${s.id}`} data-sec={s.id} className="scroll-mt-2">
                <H className={s.level <= 1 ? 'mt-5 mb-2 text-[15px] font-semibold text-slate-900 border-b border-slate-200 pb-1'
                                           : 'mt-3 mb-1.5 text-[14px] font-semibold text-slate-800'}
                   title={lang === 'en' && s.title_en ? s.title : undefined}>
                  {s.number && <span className="text-slate-500 mr-1.5">{s.number}.</span>}
                  {lang === 'en' && s.title_en ? s.title_en : s.title}
                </H>
                <div className="fr-body" dangerouslySetInnerHTML={{ __html: prepare(s.html) }} />
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
